import React, { useEffect, useState } from 'react';
import { getClientStatus } from '../services/clientService';
import { Loader2, Users, Receipt, ShieldCheck, GraduationCap, Briefcase, Coins } from 'lucide-react';

const colorsList = ['violet', 'rose', 'sky', 'teal', 'amber', 'emerald', 'indigo', 'cyan', 'lime', 'fuchsia'];

const ROLE_CONFIG = {
    admin:            { label: 'Administrateur', color: 'violet' },
    instructor_admin: { label: 'Formateur',       color: 'rose'   },
    instructor:       { label: 'Formateur',       color: 'rose'   },
    formateur:        { label: 'Formateur',       color: 'rose'   },
    secretary:        { label: 'Secrétaire',      color: 'sky'    },
    parent:           { label: 'Parent',          color: 'teal'   },
    user:             { label: 'Utilisateur',     color: 'slate'  },
    resp:             { label: 'Responsable',     color: 'indigo' }
};

const getRoleConfig = (role, loadedRoles = []) => {
    if (!role) return { label: 'Inconnu', color: 'slate' };
    const r = role.toLowerCase();
    
    // Override using DB custom roles if present
    if (loadedRoles && loadedRoles.length > 0) {
        const customRole = loadedRoles.find(dbR => dbR.name && dbR.name.toLowerCase() === r);
        if (customRole) {
            return {
                label: customRole.label || customRole.name,
                color: Object.keys(colorMap).includes(customRole.color) ? customRole.color : ROLE_CONFIG[r]?.color || colorsList[Math.abs(r.charCodeAt(0)) % colorsList.length]
            };
        }
    }

    if (ROLE_CONFIG[r]) return ROLE_CONFIG[r];
    // Hash string to pick a predictable color
    let hash = 0;
    for (let i = 0; i < role.length; i++) hash = role.charCodeAt(i) + ((hash << 5) - hash);
    const colorKey = colorsList[Math.abs(hash) % colorsList.length];
    return { label: role, color: colorKey };
};

const colorMap = {
    violet: { bg: 'bg-violet-50', border: 'border-violet-100', text: 'text-violet-700', fill: 'bg-violet-500', avatar: 'bg-violet-100 border-violet-200 text-violet-700' },
    rose:   { bg: 'bg-rose-50',   border: 'border-rose-100',   text: 'text-rose-700',   fill: 'bg-rose-500',   avatar: 'bg-rose-100 border-rose-200 text-rose-700'   },
    sky:    { bg: 'bg-sky-50',    border: 'border-sky-100',    text: 'text-sky-700',    fill: 'bg-sky-500',    avatar: 'bg-sky-100 border-sky-200 text-sky-700'    },
    teal:   { bg: 'bg-teal-50',   border: 'border-teal-100',   text: 'text-teal-700',   fill: 'bg-teal-500',   avatar: 'bg-teal-100 border-teal-200 text-teal-700'   },
    slate:  { bg: 'bg-slate-50',  border: 'border-slate-100',  text: 'text-slate-600',  fill: 'bg-slate-500',  avatar: 'bg-slate-100 border-slate-200 text-slate-600'  },
    emerald:{ bg: 'bg-emerald-50',border: 'border-emerald-100',text: 'text-emerald-700',fill: 'bg-emerald-500',avatar: 'bg-emerald-100 border-emerald-200 text-emerald-700' },
    amber:  { bg: 'bg-amber-50',  border: 'border-amber-100',  text: 'text-amber-700',  fill: 'bg-amber-500',  avatar: 'bg-amber-100 border-amber-200 text-amber-700' },
    indigo: { bg: 'bg-indigo-50', border: 'border-indigo-100', text: 'text-indigo-700', fill: 'bg-indigo-500', avatar: 'bg-indigo-100 border-indigo-200 text-indigo-700' },
    cyan:   { bg: 'bg-cyan-50',   border: 'border-cyan-100',   text: 'text-cyan-700',   fill: 'bg-cyan-500',   avatar: 'bg-cyan-100 border-cyan-200 text-cyan-700' },
    lime:   { bg: 'bg-lime-50',   border: 'border-lime-100',   text: 'text-lime-700',   fill: 'bg-lime-500',   avatar: 'bg-lime-100 border-lime-200 text-lime-700' },
    fuchsia:{ bg: 'bg-fuchsia-50',border: 'border-fuchsia-100',text: 'text-fuchsia-700',fill: 'bg-fuchsia-500',avatar: 'bg-fuchsia-100 border-fuchsia-200 text-fuchsia-700' },
};

const ClientStatus = ({ clientId, tenantName }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await getClientStatus(clientId);
                setData(res);
            } catch (err) {
                setError('Failed to load user data');
            } finally {
                setLoading(false);
            }
        };
        if (clientId) fetchData();
    }, [clientId]);

    const isLoading = loading;
    const isError = error || !data || !data.users;

    const users = data?.users || [];
    const dbRoles = data?.roles || [];
    const admins = users.filter(u => u.role === 'admin');
    // Employés = tout ce qui n'est ni 'user' ni 'admin'
    const employes = users.filter(u => u.role !== 'admin' && u.role !== 'user');
    // Étudiants = role=user ET verified=true
    const etudiants = users.filter(u => u.role === 'user' && u.verified === true);
    // Simples utilisateurs = role=user (verified ou pas)
    const simpleUsers = users.filter(u => u.role === 'user');

    // Staff affiché dans le tableau (admins + employés)
    // const staffMembers = [...admins, ...employes];

    // Tarifs
    const COST_ADMIN_DT = 7.00;
    const COST_EMPLOYE_DT = 7.00;
    const COST_USER_DT = 5.00;

    const totalCost = (
        (admins.length * COST_ADMIN_DT) +
        (employes.length * COST_EMPLOYE_DT) +
        (simpleUsers.length * COST_USER_DT)
    ).toFixed(2);

    // Pourcentages (Data Viz)
    const totalUsersCount = users.length;
    const pctUsers    = totalUsersCount > 0 ? (simpleUsers.length / totalUsersCount) * 100 : 0;
    const pctEmployes = totalUsersCount > 0 ? (employes.length / totalUsersCount) * 100 : 0;
    const pctAdmins   = totalUsersCount > 0 ? (admins.length / totalUsersCount) * 100 : 0;

    // Helper for stat card
    const StatCard = ({ icon: Icon, iconBg, iconColor, label, value, dotColor, hint }) => (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
            <div className="flex items-center gap-3 mb-3">
                <div className={`w-8 h-8 rounded-full ${iconBg} flex items-center justify-center`}>
                    <Icon size={16} className={iconColor} />
                </div>
                <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</h4>
            </div>
            <div className="text-4xl font-black text-slate-900 tracking-tighter">
                {isLoading ? <Loader2 size={24} className="animate-spin text-slate-200" /> : (isError ? <span className="text-slate-300">N/A</span> : value)}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2">
                <div className={`w-1.5 h-1.5 rounded-full ${dotColor}`}></div>
                <span className="text-[9px] text-slate-500 uppercase tracking-widest font-bold">{hint}</span>
            </div>
        </div>
    );

    return (
        <div className="space-y-5">

            {/* KPI Grid — 5 colonnes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <StatCard icon={Users}       iconBg="bg-slate-100"  iconColor="text-slate-600"  label="Total" value={users.length}     dotColor="bg-slate-400"   hint="Tous comptes" />
                <StatCard icon={GraduationCap} iconBg="bg-blue-50" iconColor="text-blue-600"   label="Étudiants" value={etudiants.length}  dotColor="bg-blue-500"  hint={`Vérifiés · ${COST_USER_DT} DT`} />
                <StatCard icon={Briefcase}   iconBg="bg-rose-50"   iconColor="text-rose-600"   label="Employés" value={employes.length}   dotColor="bg-rose-500"   hint={`Staff · ${COST_EMPLOYE_DT} DT`} />
                <StatCard icon={ShieldCheck} iconBg="bg-violet-50" iconColor="text-violet-600" label="Admins"     value={admins.length}    dotColor="bg-violet-500" hint={`Privilège · ${COST_ADMIN_DT} DT`} />
                {/* Facturation */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center">
                            <Receipt size={16} className="text-amber-600" />
                        </div>
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500">Facturation</h4>
                    </div>
                    <div className="flex items-baseline gap-1.5">
                        <div className="text-3xl font-black text-amber-600 tracking-tighter">
                            {isLoading ? <Loader2 size={24} className="animate-spin text-slate-200" /> : (isError ? <span className="text-slate-300">N/A</span> : totalCost)}
                        </div>
                        <span className="text-xs font-black text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">DT</span>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-1.5">
                        <Coins size={11} className="text-slate-400 shrink-0" />
                        <span className="text-[9px] text-slate-500 uppercase tracking-widest font-bold leading-tight">
                            {!isLoading && !isError ? `${simpleUsers.length}×5 + ${employes.length}×7 + ${admins.length}×7 DT` : 'Calcul...'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Distribution Bar */}
            {!isLoading && !isError && totalUsersCount > 0 && (
                <div>
                    <div className="flex w-full h-1.5 rounded-full overflow-hidden bg-slate-100">
                        <div style={{ width: `${pctUsers}%` }}    className="bg-blue-500 transition-all duration-500" title={`Users: ${pctUsers.toFixed(1)}%`}></div>
                        <div style={{ width: `${pctEmployes}%` }} className="bg-rose-500 transition-all duration-500" title={`Employés: ${pctEmployes.toFixed(1)}%`}></div>
                        <div style={{ width: `${pctAdmins}%` }}   className="bg-violet-500 transition-all duration-500" title={`Admins: ${pctAdmins.toFixed(1)}%`}></div>
                    </div>
                    <div className="flex items-center gap-4 mt-2">
                        <span className="flex items-center gap-1.5 text-[9px] text-slate-400 font-bold uppercase tracking-widest"><span className="w-2 h-2 rounded-full bg-blue-500 inline-block"></span>{getRoleConfig('user', dbRoles).label} ({pctUsers.toFixed(0)}%)</span>
                        <span className="flex items-center gap-1.5 text-[9px] text-slate-400 font-bold uppercase tracking-widest"><span className="w-2 h-2 rounded-full bg-rose-500 inline-block"></span>Employés ({pctEmployes.toFixed(0)}%)</span>
                        <span className="flex items-center gap-1.5 text-[9px] text-slate-400 font-bold uppercase tracking-widest"><span className="w-2 h-2 rounded-full bg-violet-500 inline-block"></span>{getRoleConfig('admin', dbRoles).label} ({pctAdmins.toFixed(0)}%)</span>
                    </div>
                </div>
            )}

            {/* Professional Overview Section instead of User List */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Cartographie des Rôles */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.02)] relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/50 rounded-bl-full -z-10"></div>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center">
                            <Briefcase size={16} className="text-indigo-600" />
                        </div>
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500">Cartographie des Rôles</h4>
                    </div>
                    {isLoading ? (
                        <div className="h-32 flex items-center justify-center"><Loader2 size={24} className="animate-spin text-slate-200" /></div>
                    ) : isError ? (
                        <div className="h-32 flex items-center justify-center text-[10px] font-black uppercase tracking-widest text-slate-300">N/A</div>
                    ) : totalUsersCount > 0 ? (
                        <div className="space-y-4">
                            {Object.entries(users.reduce((acc, u) => { acc[u.role] = (acc[u.role] || 0) + 1; return acc; }, {}))
                                .sort((a, b) => b[1] - a[1])
                                .map(([role, count]) => {
                                    const cfg = getRoleConfig(role, dbRoles);
                                    const colors = colorMap[cfg.color] || colorMap.slate;
                                    const percentage = Math.round((count / totalUsersCount) * 100) || 0;
                                    return (
                                        <div key={role} className="group">
                                            <div className="flex justify-between items-center mb-1.5">
                                                <span className={`text-[10px] font-black uppercase tracking-widest ${colors.text}`}>{cfg.label}</span>
                                                <span className="text-[11px] font-bold text-slate-600">{count} <span className="text-slate-400 font-medium">({percentage}%)</span></span>
                                            </div>
                                            <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                                                <div className={`${colors.fill} h-full rounded-full transition-all duration-700 ease-out group-hover:brightness-110`} style={{ width: `${percentage}%` }}></div>
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    ) : (
                        <div className="py-8 text-center text-sm font-medium text-slate-400 italic">Aucun rôle détecté</div>
                    )}
                </div>

                {/* Santé des Comptes */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.02)] relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50/50 rounded-bl-full -z-10"></div>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
                            <ShieldCheck size={16} className="text-emerald-600" />
                        </div>
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500">Santé des Comptes</h4>
                    </div>
                    {isLoading ? (
                        <div className="h-32 flex items-center justify-center"><Loader2 size={24} className="animate-spin text-slate-200" /></div>
                    ) : isError ? (
                        <div className="h-32 flex items-center justify-center text-[10px] font-black uppercase tracking-widest text-slate-300">N/A</div>
                    ) : (
                        <div className="flex flex-col justify-center h-[calc(100%-3rem)] gap-3">
                            <div className="flex items-center justify-between p-4 rounded-2xl border border-emerald-100 bg-emerald-50/40 hover:bg-emerald-50/80 transition-colors">
                                <div>
                                    <h5 className="text-[11px] font-black uppercase tracking-widest text-emerald-800">Comptes Vérifiés</h5>
                                    <p className="text-[10px] font-medium text-emerald-600/70 mt-0.5">Accès actif autorisé</p>
                                </div>
                                <div className="text-3xl font-black text-emerald-600 tracking-tighter">{users.filter(u => u.verified).length}</div>
                            </div>
                            <div className="flex items-center justify-between p-4 rounded-2xl border border-amber-100 bg-amber-50/40 hover:bg-amber-50/80 transition-colors">
                                <div>
                                    <h5 className="text-[11px] font-black uppercase tracking-widest text-amber-800">En Attente</h5>
                                    <p className="text-[10px] font-medium text-amber-600/70 mt-0.5">Vérification requise</p>
                                </div>
                                <div className="text-3xl font-black text-amber-500 tracking-tighter">{users.filter(u => !u.verified).length}</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
};

export default ClientStatus;



